import { useSvgTextWidth } from '@/hooks/useSvgTextWidth';
import type { PriceDistributionBucket } from '@/types/session';
import {
  formatDistributionPrice,
  formatDistributionStrikes,
} from './priceDistributionFormat.utils';
import { priceToRowY } from './railCharts.utils';

interface PriceDistributionChartProps {
  rows: readonly PriceDistributionBucket[];
  /** Null for a past session without a close: no marker rather than the live spot. */
  currentPrice: number | null;
  width: number;
  /** Height to fill in the fit layout; 0 sizes the chart from its rows. */
  fillHeight: number;
}

const TOP = 4;
const AXIS_SPACE = 26;

export function PriceDistributionChart({
  rows,
  currentPrice,
  width,
  fillHeight,
}: PriceDistributionChartProps) {
  const strikeLabels = formatDistributionStrikes(rows.map((row) => row.strike));
  const priceLabel = formatDistributionPrice(currentPrice);
  const longestStrike = strikeLabels.reduce((longest, label) => {
    return label.length > longest.length ? label : longest;
  }, '');
  const [strikeMeasureRef, strikeWidth] = useSvgTextWidth(longestStrike);
  const [priceMeasureRef, priceWidth] = useSvgTextWidth(priceLabel);
  const left = Math.max(40, strikeWidth + 14);
  const right = Math.max(66, priceWidth + 22);
  const w = Math.max(240, width);
  const count = rows.length;
  const fills = fillHeight > 120;
  const rowH = fills ? Math.min(20, (fillHeight - TOP - AXIS_SPACE) / count) : 16;
  const height = fills ? fillHeight : TOP + count * rowH + AXIS_SPACE;
  const plotBottom = TOP + count * rowH;
  const labelEvery = Math.max(1, Math.ceil(15 / rowH));
  const labelOffset = labelEvery > 1 ? Math.floor(labelEvery / 2) : 0;
  const pad = rowH > 9 ? 2 : rowH > 5 ? 1 : 0.4;
  const maxPercent = Math.max(...rows.map((row) => row.percent), 0);
  // 5% gridlines as in the reference; widen to 10% once labels would collide.
  const tickStep = maxPercent > 25 ? 10 : 5;
  const tickMax = Math.max(5, Math.ceil(maxPercent / tickStep) * tickStep);
  const x = (percent: number) => left + (percent / tickMax) * (w - left - right);
  const ticks = Array.from({ length: tickMax / tickStep + 1 }, (_, index) => index * tickStep);
  // Keep the 5%/10% grid, but thin tick labels when currency gutters narrow the plot.
  const tickLabelEvery = Math.max(1, Math.ceil(36 / ((w - left - right) / (ticks.length - 1))));
  const currentY =
    currentPrice == null
      ? null
      : priceToRowY(
          rows.map((row) => row.strike),
          currentPrice,
          TOP,
          rowH,
        );

  return (
    <svg
      role="img"
      aria-label="Time spent percent at each strike"
      width={w}
      height={height}
      viewBox={`0 0 ${w} ${height}`}
      className="block w-full font-sans text-xs [&_text]:fill-soft"
    >
      <g visibility="hidden" aria-hidden="true" className="font-mono">
        <text ref={strikeMeasureRef}>{longestStrike}</text>
        <text ref={priceMeasureRef}>{priceLabel}</text>
      </g>
      {ticks.map((tick, index) => (
        <g key={tick}>
          <line x1={x(tick)} x2={x(tick)} y1={TOP} y2={plotBottom} stroke="rgba(255,255,255,.05)" />
          {(index === 0 ||
            index === ticks.length - 1 ||
            (index % tickLabelEvery === 0 && ticks.length - 1 - index >= tickLabelEvery)) && (
            <text x={x(tick)} y={plotBottom + 20} textAnchor="middle">
              {tick}%
            </text>
          )}
        </g>
      ))}
      <line x1={left} x2={left} y1={TOP} y2={plotBottom} stroke="rgba(255,255,255,.25)" />
      {rows.map((row, index) => {
        const y = TOP + index * rowH;
        return (
          <g key={row.strike}>
            <rect
              x={left + 1}
              y={y + pad}
              width={Math.max(0, x(row.percent) - left)}
              height={Math.max(1, rowH - 2 * pad)}
              fill="var(--bar)"
            >
              <title>{`${strikeLabels[index]} · ${row.percent.toFixed(1)}%`}</title>
            </rect>
            {index % labelEvery === labelOffset && (
              <text x={left - 10} y={y + rowH / 2 + 4} textAnchor="end" className="font-mono">
                {strikeLabels[index]}
              </text>
            )}
          </g>
        );
      })}
      {currentY != null && (
        <g>
          <line
            x1={left}
            x2={w - right + 6}
            y1={currentY}
            y2={currentY}
            stroke="#facc15"
            strokeWidth={2}
            strokeDasharray="6 5"
          />
          <rect
            x={w - right + 8}
            y={currentY - 11}
            width={right - 10}
            height={22}
            rx={3}
            fill="#0a0a0a"
            stroke="#facc15"
          />
          <text
            x={w - right + 8 + (right - 10) / 2}
            y={currentY + 4}
            textAnchor="middle"
            className="font-mono"
            style={{ fill: '#facc15' }}
          >
            {priceLabel}
          </text>
        </g>
      )}
    </svg>
  );
}
